import { Link } from "@tanstack/react-router";
import type { CSSProperties, ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { cn } from "../lib/utils";
import type { EtatLivre, Gabarit, Provenance } from "./contract";
import { LIBELLE_PROVENANCE, type Pages } from "./histoires-textes";
import { statutDe } from "./queries";
import { EcranDErreur, ENCADRE_PANNE } from "./states";

/**
 * Ce que les ecrans du module Histoires partagent : le fil d'Ariane, la double introuvable, les
 * badges (provenance, etat d'un livre), les encadres, la zone grise de l'illustration et ses
 * gabarits. Rien n'y vient du coloriage : un module n'importe jamais un autre module.
 */

/** Les liens poses comme des boutons (`Button` du template ne rend qu'un `button`). */
export const LIEN_BOUTON =
  "inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-left text-sm font-medium transition hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
export const LIEN_BOUTON_PLEIN =
  "inline-flex min-h-10 items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-left text-sm font-medium text-primary-foreground transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
/** Un bouton en contour qui garde une `ref` (celui du template ne la transmet pas). */
export const BOUTON_CONTOUR = cn(LIEN_BOUTON, "disabled:pointer-events-none disabled:opacity-50");
/** Un lien qui a l'air d'un lien, souligne en gris : « Modifier », « Voir le brief ». */
export const LIEN_TEXTE =
  "font-medium underline decoration-muted-foreground/60 underline-offset-[3px] hover:decoration-current focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm";

export const ALERTE_GRISE = "rounded-md border border-border bg-muted px-3.5 py-2.5 text-sm";
export const ALERTE_JAUNE =
  "rounded-md border border-avertissement/60 bg-avertissement/15 px-3.5 py-2.5 text-sm";
export const ALERTE_ROUGE =
  "rounded-md border border-interdit/40 bg-interdit/15 px-3.5 py-2.5 text-sm text-interdit";
/**
 * L'encadre des pannes (Lab a l'arret, session perdue, refus) : celui de la relecture echouee
 * (`states.tsx`), a 4,5:1 au moins dans les deux themes.
 */
export const ALERTE_PANNE = ENCADRE_PANNE;

/** Les champs de saisie du module. */
export const CHAMP =
  "w-full rounded-md border border-input bg-background px-2.5 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";

/**
 * Le fil d'Ariane : `Histoires › livre › double` sur grand ecran ; au telephone, un seul lien
 * vers le parent, precede de « ‹ » (maquette `HistoiresDoubleMobile.dc.html`).
 */
export function FilHistoires({
  livre,
  children,
}: {
  /** Le livre, en lien : absent sur la page du livre lui-meme. */
  livre?: { readonly id: number; readonly titre: string } | undefined;
  /** Le dernier maillon, en texte. */
  children?: ReactNode;
}) {
  const separateur = (
    <span aria-hidden="true" className="max-md:hidden">
      {" › "}
    </span>
  );
  return (
    <nav aria-label="Fil d'Ariane" className="text-sm text-muted-foreground">
      <span aria-hidden="true" className="md:hidden">
        {"‹ "}
      </span>
      <Link
        className={cn("underline-offset-2 hover:underline", livre !== undefined && "max-md:hidden")}
        to="/histoires"
      >
        Histoires
      </Link>
      {livre === undefined ? null : (
        <>
          {separateur}
          <Link
            className="underline-offset-2 hover:underline"
            params={{ id: String(livre.id) }}
            to="/histoires/livres/$id"
          >
            {livre.titre}
          </Link>
        </>
      )}
      {children === undefined || children === null ? null : (
        <span className="max-md:hidden">
          {separateur}
          {children}
        </span>
      )}
    </nav>
  );
}

/** 404 dans le module : il ramene aux livres, pas au portefeuille. */
export function IntrouvableHistoires({ quoi }: { quoi: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Rien sous ce nom</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm text-muted-foreground">
        <p>Les histoires ne connaissent pas {quoi}.</p>
        <p>
          <Link className="underline underline-offset-2" to="/histoires">
            Revenir aux livres
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}

/** Les erreurs de lecture : 404 a la facon du module, le reste par `EcranDErreur`. */
export function ErreurHistoires({ error, quoi }: { error: unknown; quoi: string }) {
  if (statutDe(error) === 404) return <IntrouvableHistoires quoi={quoi} />;
  return <EcranDErreur error={error} quoi={quoi} />;
}

const TON_PROVENANCE: Readonly<Record<Provenance, string>> = {
  ecrit: "bg-provenance-ecrit/15 text-provenance-ecrit",
  assiste: "bg-provenance-assiste/15 text-provenance-assiste",
  genere: "bg-provenance-genere/15 text-provenance-genere",
};

/** Le badge de provenance : « écrit », « assisté », « généré », ou « vide » en pointille. */
export function BadgeProvenance({
  provenance,
  compte,
}: {
  provenance: Provenance | null;
  /** Un nombre de doubles, pose apres le nom : « écrit · 2 ». */
  compte?: number | undefined;
}) {
  const libelle = provenance === null ? "vide" : LIBELLE_PROVENANCE[provenance];
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold",
        provenance === null
          ? "border border-dashed border-muted-foreground/60 text-muted-foreground"
          : TON_PROVENANCE[provenance],
      )}
    >
      {compte === undefined ? libelle : `${libelle} · ${compte}`}
    </span>
  );
}

const LIBELLE_ETAT: Readonly<Record<EtatLivre, string>> = {
  ouvert: "ouvert",
  envoye: "envoyé",
  en_ligne: "en ligne",
  clos: "clos",
};

/**
 * Les teintes des etapes du portefeuille (`theme.css`, contrastes mesures, texte sur sa teinte
 * a 15 %) : un livre ouvert est en construction, un livre envoye attend KDP, un livre en ligne
 * vit. Un livre clos passe en gris.
 */
const TON_ETAT: Readonly<Record<EtatLivre, string>> = {
  ouvert: "bg-etape-build/15 text-etape-build",
  envoye: "bg-etape-test/15 text-etape-test",
  en_ligne: "bg-etape-live/15 text-etape-live",
  clos: "bg-muted text-muted-foreground",
};

export function BadgeEtatLivre({ etat }: { etat: EtatLivre }) {
  return (
    <span
      className={cn(
        "inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold",
        TON_ETAT[etat],
      )}
    >
      {LIBELLE_ETAT[etat]}
    </span>
  );
}

/** Un signal d'une carte : un carre de couleur et sa phrase, en rouge ou en ambre. */
export function Signal({ ton, children }: { ton: "rouge" | "ambre"; children: ReactNode }) {
  return (
    <p
      className={cn(
        "flex items-start gap-1.5 text-xs font-medium leading-snug",
        ton === "rouge" ? "text-interdit" : "text-fourchette",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "mt-[3px] size-[7px] flex-none rounded-[2px]",
          ton === "rouge" ? "bg-interdit" : "bg-fourchette",
        )}
      />
      <span>{children}</span>
    </p>
  );
}

// ---------------------------------------------------------------------------------------
// La zone grise de l'illustration et la place du texte de chaque gabarit.

type Place = {
  /** Les cadres de texte, en pourcentage de la double (deux pages cote a cote). */
  readonly cadres: readonly CSSProperties[];
  /** La page laissee blanche, pour les gabarits ou l'illustration ne tient qu'une page. */
  readonly blanche: "gauche" | "droite" | "deux" | null;
};

/** La place du texte de chaque gabarit, reprise des vignettes de la maquette. */
export const PLACE_DU_TEXTE: Readonly<Record<Gabarit, Place>> = {
  bandeau_bas: {
    cadres: [{ left: "7%", right: "7%", bottom: "9%", height: "24%" }],
    blanche: null,
  },
  bandeau_haut: { cadres: [{ left: "7%", right: "7%", top: "9%", height: "24%" }], blanche: null },
  texte_gauche: {
    cadres: [{ left: "7%", top: "11%", width: "33%", height: "46%" }],
    blanche: null,
  },
  texte_droite: {
    cadres: [{ left: "60%", top: "11%", width: "33%", height: "46%" }],
    blanche: null,
  },
  image_gauche: {
    cadres: [{ left: "57%", top: "18%", width: "36%", height: "64%" }],
    blanche: "droite",
  },
  image_droite: {
    cadres: [{ left: "7%", top: "18%", width: "36%", height: "64%" }],
    blanche: "gauche",
  },
  texte_seul: {
    cadres: [
      { left: "7%", top: "18%", width: "36%", height: "64%" },
      { left: "57%", top: "18%", width: "36%", height: "64%" },
    ],
    blanche: "deux",
  },
};

const LIGNES_DU_CADRE = ["92%", "100%", "84%", "96%", "58%"];

/**
 * La zone grise qui attend l'illustration choisie : le pli au milieu, les numeros de page en
 * bas, et en pointille la place du texte du gabarit (maquettes `HistoiresLivre` et
 * `HistoiresDouble`). Muette : ce qu'elle montre est dit en texte a cote.
 */
export function ZoneIllustration({
  gabarit,
  pages,
  grande = false,
}: {
  gabarit: Gabarit | null;
  pages?: Pages | null | undefined;
  /** La zone de l'ecran d'une double : le cadre porte ses lignes de texte. */
  grande?: boolean;
}) {
  const place = gabarit === null ? null : PLACE_DU_TEXTE[gabarit];
  return (
    <div
      aria-hidden="true"
      className="relative aspect-[2/1] w-full overflow-hidden rounded-md bg-muted-foreground/15"
    >
      {place?.blanche === "gauche" || place?.blanche === "deux" ? (
        <span className="absolute inset-y-0 left-0 w-1/2 bg-card" />
      ) : null}
      {place?.blanche === "droite" || place?.blanche === "deux" ? (
        <span className="absolute inset-y-0 right-0 w-1/2 bg-card" />
      ) : null}
      <span className="absolute inset-y-0 left-1/2 border-l border-dashed border-muted-foreground/50" />
      {place?.cadres.map((cadre) => (
        <span
          className={cn(
            "absolute flex flex-col gap-2 rounded-[4px] border-dashed border-muted-foreground bg-card/60",
            grande ? "border-[1.5px] p-2.5" : "border",
          )}
          key={JSON.stringify(cadre)}
          style={cadre}
        >
          {grande
            ? LIGNES_DU_CADRE.map((largeur) => (
                <i
                  className="block h-[5px] rounded-full bg-muted-foreground/35"
                  key={largeur}
                  style={{ width: largeur }}
                />
              ))
            : null}
        </span>
      ))}
      {pages === null || pages === undefined ? null : (
        <>
          <span className="absolute bottom-1 left-2 text-[11px] tabular-nums text-muted-foreground">
            {pages.debut}
          </span>
          {pages.fin === pages.debut ? null : (
            <span className="absolute right-2 bottom-1 text-[11px] tabular-nums text-muted-foreground">
              {pages.fin}
            </span>
          )}
        </>
      )}
    </div>
  );
}

/** La vignette d'un gabarit, dans son bouton de choix (72 x 36 px, comme la maquette). */
export function VignetteGabarit({ gabarit }: { gabarit: Gabarit }) {
  const place = PLACE_DU_TEXTE[gabarit];
  return (
    <span
      aria-hidden="true"
      className={cn(
        "relative block h-9 w-[72px] flex-none overflow-hidden rounded-[3px]",
        place.blanche === "deux"
          ? "bg-card shadow-[inset_0_0_0_1px_var(--color-border)]"
          : "bg-muted-foreground/30",
      )}
    >
      {place.blanche === "gauche" ? (
        <span className="absolute inset-y-0 left-0 w-1/2 bg-card shadow-[inset_0_0_0_1px_var(--color-border)]" />
      ) : null}
      {place.blanche === "droite" ? (
        <span className="absolute inset-y-0 right-0 w-1/2 bg-card shadow-[inset_0_0_0_1px_var(--color-border)]" />
      ) : null}
      <span className="absolute inset-y-0 left-1/2 border-l border-dashed border-muted-foreground/70" />
      {place.blanche === null
        ? place.cadres.map((cadre) => (
            <span
              className="absolute rounded-[2px] border border-muted-foreground bg-card"
              key={JSON.stringify(cadre)}
              style={cadre}
            />
          ))
        : place.cadres.map((cadre) => (
            <span
              className="absolute flex flex-col justify-center gap-[3px]"
              key={JSON.stringify(cadre)}
              style={cadre}
            >
              <u className="block h-0.5 w-full rounded-full bg-muted-foreground" />
              <u className="block h-0.5 w-4/5 rounded-full bg-muted-foreground" />
              <u className="block h-0.5 w-11/12 rounded-full bg-muted-foreground" />
            </span>
          ))}
    </span>
  );
}
